import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  Dropdown,
  Upload,
  Progress,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  FileOutlined,
  EyeOutlined,
  PlusOutlined,
  ExportOutlined,
  ImportOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import MediaSelector from '../components/MediaLibrary/MediaSelector';
import type { ColumnsType } from 'antd/es/table';
import documentService, { DocumentRecord as ApiDocumentRecord, DocumentListParams } from '../services/document';
import mediaService from '../services/media';
import { NATION_OPTIONS } from '../config/nations';
import userPreferenceService from '../services/userPreference';
import { usePermissions } from '../hooks/usePermissions';
import { getApiResponseErrorMessage } from '../utils/error';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { filterActionColumnByAccess, hasAnyRowActionAccess } from '../utils/action-permissions';
import ListHelpButton from '../components/ListHelpButton';

const { Option } = Select;
const { TextArea } = Input;

// 使用从服务导入的接口，并扩展本地需要的字段
type DocumentRecord = ApiDocumentRecord & {
  file_url?: string;
}

const DocumentManagement: React.FC = () => {
  useDocumentTitle('文件管理');
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [files, setFiles] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFile, setEditingFile] = useState<DocumentRecord | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [nationFilter, setNationFilter] = useState('');
  const permissions = usePermissions();
  const canCreate = permissions.hasActionAccess('document-management', 'create');
  const canEdit = permissions.hasActionAccess('document-management', 'edit');
  const canDelete = permissions.hasActionAccess('document-management', 'delete');
  const canExport = permissions.hasActionAccess('document-management', 'export');
  const canImport = permissions.hasActionAccess('document-management', 'import');
  const canViewDetail = permissions.hasActionAccess('document-management', 'view_details');
  const hasRowActionAccess = hasAnyRowActionAccess(permissions, 'document-management', ['view_details', 'edit', 'delete']);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // 从 URL 参数初始化分页状态
  const [pagination, setPagination] = useState({
    current: parseInt(urlSearchParams.get('page') || '1', 10),
    pageSize: parseInt(urlSearchParams.get('pageSize') || '10', 10),
    total: 0,
  });

  // 用于跟踪是否已经初始化
  const isInitialized = useRef(false);

  // 状态选项
  const statusOptions = [
    { value: 'active', label: '有效', color: 'green' },
    { value: 'pending', label: '待审核', color: 'orange' },
    { value: 'archived', label: '已归档', color: 'gray' },
    { value: 'deleted', label: '已删除', color: 'red' },
  ];

  // 国家选项
  const nationOptions = NATION_OPTIONS;

  // 文件类型选项
  const fileTypeOptions = [
    { value: 'document', label: '文档', icon: <FileOutlined /> },
    { value: 'image', label: '图片', icon: <FileOutlined /> },
    { value: 'template', label: '模板', icon: <FileOutlined /> },
    { value: 'certificate', label: '证书', icon: <FileOutlined /> },
    { value: 'contract', label: '合同', icon: <FileOutlined /> },
    { value: 'other', label: '其他', icon: <FileOutlined /> },
  ];

  // 获取文件数据
  const fetchFiles = async (params?: DocumentListParams) => {
    setLoading(true);
    try {
      const response = await documentService.getFiles(params);
      if (response.success) {
        setFiles(response.data.data);
        // 只更新 total 和 current，避免触发分页循环
        setPagination(prev => ({
          ...prev,
          current: response.data.current_page,
          total: response.data.total,
          // 不更新 pageSize，因为它应该由用户偏好控制
        }));
      }
    } catch (error) {
      message.error('获取文件数据失败');
      console.error('获取文件数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPageSizeSettings();
    // 延迟标记初始化完成，确保分页状态已设置
    setTimeout(() => {
      isInitialized.current = true;
      // 初始化完成后获取第一次数据
      fetchFiles({
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText,
        nation: nationFilter || undefined,
      });
    }, 0);
  }, []);

  // 搜索、筛选变化时重置到第一页
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    if (pagination.current !== 1) {
      const newPagination = { ...pagination, current: 1 };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      fetchFiles({
        page: 1,
        per_page: pagination.pageSize,
        search: searchText,
        nation: nationFilter || undefined,
      });
    }
  }, [searchText, nationFilter]);

  // 分页变化时获取数据
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    if (pagination.current > 0 && pagination.pageSize > 0) {
      fetchFiles({
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText,
        nation: nationFilter || undefined,
      });
    }
  }, [pagination.current, pagination.pageSize]);

  // 同步分页状态到 URL
  const updateUrlParams = (newPagination: { current: number; pageSize: number; total?: number }) => {
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.set('page', newPagination.current.toString());
    newSearchParams.set('pageSize', newPagination.pageSize.toString());
    setUrlSearchParams(newSearchParams, { replace: true });
  };

  // 加载每页条数设置
  const loadPageSizeSettings = async () => {
    try {
      const savedPageSize = await userPreferenceService.getPageSizeSettings('documents');
      // 优先使用 URL 参数，如果没有则使用保存的偏好
      const urlPageSize = parseInt(urlSearchParams.get('pageSize') || '0', 10);
      const finalPageSize = urlPageSize > 0 ? urlPageSize : savedPageSize;

      // 只更新 pageSize，不更新 current，避免覆盖 URL 中的页码
      if (pagination.pageSize !== finalPageSize) {
        setPagination(prev => ({
          ...prev,
          pageSize: finalPageSize
        }));
        // 同步 pageSize 到 URL（保持当前页码不变）
        const newSearchParams = new URLSearchParams(urlSearchParams);
        newSearchParams.set('pageSize', finalPageSize.toString());
        setUrlSearchParams(newSearchParams, { replace: true });
      }
    } catch (error) {
      console.error('加载每页条数设置失败:', error);
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // 渲染文件名称（仅记录名称）
  const renderFileName = (name: string) => {
    return (
      <Space>
        <FileOutlined />
        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
          {name}
        </span>
      </Space>
    );
  };

  // 渲染媒体文件列表
  const renderMediaFiles = (record: DocumentRecord) => {
    const mediaFilesInfo = (record as any).media_files_info || [];

    if (mediaFilesInfo.length === 0) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>无文件</span>;
    }

    return (
      <div style={{ maxWidth: '300px' }}>
        {mediaFilesInfo.map((mediaInfo: any, index: number) => (
          <div key={index} style={{
            marginBottom: index < mediaFilesInfo.length - 1 ? 4 : 0,
          }}>
            <span
              onClick={() => mediaService.downloadMedia(mediaInfo.id, mediaInfo.name)}
              style={{
                padding: 0,
                fontSize: '12px',
                color: '#1890ff',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '280px',
                cursor: 'pointer'
              }}
              title={mediaInfo.name}
            >
              <Space size={4}>
                <DownloadOutlined />
                {mediaInfo.name}
              </Space>
            </span>
          </div>
        ))}
      </div>
    );
  };

  // 表格列定义（基础列）
  const baseColumns: ColumnsType<DocumentRecord> = useMemo(() => {
    const columns: ColumnsType<DocumentRecord> = [
      {
        title: '文件名称',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        fixed: 'left',
        render: renderFileName,
      },
      {
        title: '适用地区',
        dataIndex: 'nation',
        key: 'nation',
        width: 120,
        render: (nation: string) => {
          const option = nationOptions.find(opt => opt.value === nation);
          return option?.label || nation;
        },
      },
      {
        title: '媒体文件',
        key: 'media_files',
        width: 300,
        render: (_, record: DocumentRecord) => renderMediaFiles(record),
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        width: 200,
        render: (remark: string) => (
          <div style={{
            maxWidth: '180px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={remark}>
            {remark || <span style={{ color: '#999', fontStyle: 'italic' }}>暂无说明</span>}
          </div>
        ),
      },
      // {
      //   title: '创建时间',
      //   dataIndex: 'created_at',
      //   key: 'created_at',
      //   width: 150,
      //   render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
      // },
    ];

    if (hasRowActionAccess) {
      columns.push({
        title: '操作',
        key: 'actions',
        fixed: 'right',
        width: 100,
        render: (_, record: DocumentRecord) => (
          <Space>
            {canViewDetail && (
              <Button
                icon={<EyeOutlined />}
                onClick={() => navigate(`/documents/${record.id}`)}
                size="small"
                title="查看"
              />
            )}
            {canEdit && (
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                size="small"
                title="编辑"
              />
            )}
            {canDelete && (
              <Popconfirm
                title="确定删除这个文件吗？"
                onConfirm={() => record.id && handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  title="删除"
                />
              </Popconfirm>
            )}
          </Space>
        ),
      });
    }

    return columns;
  }, [canDelete, canEdit, canViewDetail, hasRowActionAccess, nationOptions]);

  // 依据列显示权限过滤
  const columns: ColumnsType<DocumentRecord> = useMemo(() => {
    const candidate = filterActionColumnByAccess(
      baseColumns.map((c: any) => ({ ...c, key: c.key || c.dataIndex })),
      hasRowActionAccess
    );
    const permitted = permissions.getVisibleColumns('document-management', candidate as any);
    return permitted.map((c: any) => ({ ...c }));
  }, [baseColumns, hasRowActionAccess, permissions]);






  // 处理新增/编辑
  const handleEdit = (file?: DocumentRecord) => {
    setEditingFile(file || null);
    if (file) {
      const formValues = { ...file } as any;
      form.setFieldsValue({ ...formValues, files: Array.isArray(formValues.files) ? formValues.files : [] });
    } else {
      form.resetFields();
      form.setFieldsValue({ files: [] });
    }
    setModalVisible(true);
  };


  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // files 必须是数组
      const selectedFileIds = Array.isArray(values.files) ? values.files : [];

      // 检查是否有文件被选择
      if (!editingFile && selectedFileIds.length === 0) {
        message.error('请选择至少一个文件');
        return;
      }

      const fileData = {
        ...values,
        files: selectedFileIds, // 后端期望数组格式
      };

      if (editingFile) {
        // 编辑文件记录
        const response = await documentService.updateFile(editingFile.id!, fileData);
        if (response.success) {
          message.success('文件信息更新成功');
        } else {
          message.error(getApiResponseErrorMessage(response, '更新失败'));
          return;
        }
      } else {
        // 新增文件记录
        const response = await documentService.createFile(fileData);
        if (response.success) {
          message.success(`文件记录添加成功，包含 ${selectedFileIds.length} 个文件`);
        } else {
          message.error(getApiResponseErrorMessage(response, '创建失败'));
          return;
        }
      }

      setModalVisible(false);
      setEditingFile(null);
      form.resetFields();
      fetchFiles({
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText,
        nation: nationFilter || undefined,
      });
    } catch (error) {
      message.error('保存失败');
      console.error('保存文件失败:', error);
    }
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    try {
      const response = await documentService.deleteFile(id);
      if (response.success) {
        message.success('文件删除成功');
        fetchFiles({
          page: pagination.current,
          per_page: pagination.pageSize,
          search: searchText,
          nation: nationFilter || undefined,
        });
      } else {
        message.error(getApiResponseErrorMessage(response, '删除失败'));
      }
    } catch (error) {
      message.error('删除失败');
      console.error('删除文件失败:', error);
    }
  };

  // 导出数据
  const handleExport = async () => {
    try {
      const blob = await documentService.exportDocuments({
        search: searchText || undefined,
        nation: nationFilter || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `文档数据_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    }
  };

  // 导入数据
  const handleImport = async (file: any) => {
    setImportLoading(true);
    setImportProgress(0);

    try {
      const result = await documentService.importDocuments(file);
      setImportProgress(100);

      if (result.success) {
        const { total = 0, success = 0, updated = 0, failed = 0, errors = [] } = result.data || {};
        let messageText = `导入完成！总计: ${total}, 成功: ${success}, 更新: ${updated}`;

        if (failed > 0) {
          messageText += `, 失败: ${failed}`;
          if (errors && errors.length > 0) {
            messageText += `\n错误详情: ${errors.slice(0, 3).join('; ')}`;
            if (errors.length > 3) {
              messageText += '...';
            }
          }
        }

        if (failed === 0) {
          message.success(messageText);
        } else {
          message.warning(messageText);
        }

        fetchFiles({
          page: pagination.current,
          per_page: pagination.pageSize,
          search: searchText,
          nation: nationFilter || undefined,
        });
      } else {
        message.error(`导入失败: ${result.message || '未知错误'}`);
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请检查文件格式');
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      setImportModalVisible(false);
    }
  };

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    fetchFiles({
      page: 1,
      per_page: pagination.pageSize,
      search: value,
      nation: nationFilter || undefined,
    });
  };

  // 处理国家筛选
  const handleNationChange = (value: string) => {
    setNationFilter(value);
    setPagination({ ...pagination, current: 1 });
    fetchFiles({
      page: 1,
      per_page: pagination.pageSize,
      search: searchText,
      nation: value || undefined,
    });
  };

  // 重置筛选
  const handleResetFilters = () => {
    setSearchText('');
    setNationFilter('');
    setPagination({ ...pagination, current: 1 });
    fetchFiles({
      page: 1,
      per_page: pagination.pageSize,
      search: undefined,
      nation: undefined,
    });
  };

  // 处理表格变化
  const handleTableChange = async (paginationConfig: any) => {
    const isPageSizeChanged = pagination.pageSize !== paginationConfig.pageSize;

    // 如果每页条数发生变化，保存用户偏好并从第一页开始
    if (isPageSizeChanged) {
      try {
        await userPreferenceService.savePageSizeSettings('documents', paginationConfig.pageSize);
      } catch (error) {
        console.error('保存每页条数设置失败:', error);
      }

      const newPagination = {
        ...pagination,
        current: 1, // 从第一页开始
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
    } else {
      // 只是翻页，保持当前配置
      const newPagination = {
        ...pagination,
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* 表头th不换行，避免列标题换行导致高度不一致 */}
      <style>{`.doc-table .ant-table-thead > tr > th { white-space: nowrap; }`}</style>
      <Card
        title={(
          <Space size="small">
            <span>文档管理</span>
            <ListHelpButton page_key="document_management" />
          </Space>
        )}
        extra={
          <Space>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleEdit()} data-testid="btn-doc-upload">
                上传文件
              </Button>
            )}

            {(() => {
              const items: any[] = [];
              if (canExport) items.push({ key: 'export', label: '导出数据', icon: <ExportOutlined />, onClick: handleExport });
              if (canImport) items.push({ key: 'import', label: '导入数据', icon: <ImportOutlined />, onClick: () => setImportModalVisible(true) });
              return items.length > 0 ? (
                <Dropdown menu={{ items }}>
                  <Button data-testid="btn-doc-data-menu">数据管理</Button>
                </Dropdown>
              ) : null;
            })()}

            {canDelete && selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定删除选中的 ${selectedRowKeys.length} 条记录吗？`}
                okText="确定"
                cancelText="取消"
                onConfirm={async () => {
                  const ids = selectedRowKeys as number[];
                  try {
                    const response = await documentService.batchDeleteDocuments(ids);
                    if (response.success) {
                      message.success(response.message || `成功删除 ${response.data?.deleted_count || ids.length} 个文档`);
                      setSelectedRowKeys([]);
                      fetchFiles({
                        page: pagination.current,
                        per_page: pagination.pageSize,
                        search: searchText,
                        nation: nationFilter || undefined,
                      });
                    } else {
                      message.error(response.message || '批量删除失败');
                    }
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || '批量删除失败');
                  }
                }}
                disabled={!selectedRowKeys.length}
              >
                <Button danger disabled={!selectedRowKeys.length}>批量删除</Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Input.Search
              placeholder="搜索文件名、说明"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              allowClear
              data-testid="doc-search"
            />
          </Col>
          <Col span={3}>
            <Select
              placeholder="选择国家"
              style={{ width: '100%' }}
              value={nationFilter || undefined}
              onChange={handleNationChange}
              allowClear
              showSearch
              data-testid="doc-nation-filter"
            >
              {nationOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* 筛选状态显示 */}
        {(searchText || nationFilter) && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <Space wrap>
                  <span>已应用筛选条件：</span>
                  {searchText && (
                    <Tag closable onClose={() => { setSearchText(''); handleSearch(''); }}>
                      搜索: {searchText}
                    </Tag>
                  )}
                  {nationFilter && (
                    <Tag closable onClose={() => handleNationChange('')}>
                      国家: {nationOptions.find(opt => opt.value === nationFilter)?.label || nationFilter}
                    </Tag>
                  )}
                  <Button type="link" size="small" onClick={handleResetFilters}>
                    清除所有筛选
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <Table
          className="doc-table"
          columns={columns}
          dataSource={files}
          loading={loading}
          scroll={{ x: 1000 }}
          sticky={{ offsetHeader: 0 }}
          rowSelection={canDelete ? ({ selectedRowKeys, onChange: setSelectedRowKeys as any, preserveSelectedRowKeys: true } as any) : undefined}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
          onChange={handleTableChange}
          rowKey="id"
        />
      </Card>

      {/* 导入模态框 */}
      <Modal
        title="导入文档数据"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportProgress(0);
        }}
        footer={null}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Upload.Dragger
            name="file"
            accept=".xlsx,.xls,.csv"
            beforeUpload={(file) => {
              handleImport(file);
              return false;
            }}
            showUploadList={false}
            disabled={importLoading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">支持 .xlsx, .xls, .csv 格式文件，单个文件不超过10MB</p>
          </Upload.Dragger>

          {importLoading && (
            <div style={{ marginTop: 20 }}>
              <Progress percent={importProgress} />
              <p>导入中，请稍候...</p>
            </div>
          )}

          <div style={{ marginTop: 20, textAlign: 'left' }}>
            <ul style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
              <li>请使用模板整理数据，文件名称与国家/地区为必填项</li>
              <li>文件ID列表为媒体库ID，可填写JSON数组或以逗号分隔</li>
              <li>同名且同国家的记录将更新，否则新增</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* 上传/编辑模态框 */}
      <Modal
        title={editingFile ? '编辑文件信息' : '上传文件'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnHidden
        data-testid="modal-document"
        okButtonProps={{ 'data-testid': 'btn-doc-save' } as any}
        cancelButtonProps={{ 'data-testid': 'btn-doc-cancel' } as any}
      >
        <Form
          form={form}
          layout="vertical"
        >
          {/* 隐藏的 files 字段：直接存储文件ID数组 */}
          <Form.Item name="files" hidden />

          {/* 仅在 files 字段变化时才重新渲染媒体选择区域，避免其它输入触发重复拉取 /api/media/:id */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => (prev as any).files !== (curr as any).files}
          >
            {({getFieldValue, setFieldsValue}) => {
              const filesValue = getFieldValue('files');
              const value = Array.isArray(filesValue) ? filesValue : [];

              return (
                <Form.Item label="关联文件">
                  <div style={{
                    border: '2px dashed #d9d9d9',
                    borderRadius: '6px',
                    padding: '12px'
                  }} data-testid="doc-media-selector">
                    <MediaSelector
                      multiple={true}
                      placeholder="选择或上传文件"
                      fileTypes={['image', 'document', 'video', 'audio', 'other']}
                      showPreview={true}
                      value={value}
                      onChange={(selectedIds) => {
                        const arr = Array.isArray(selectedIds) ? selectedIds : (selectedIds ? [selectedIds] : []);
                        setFieldsValue({ files: arr });
                      }}
                    />
                  </div>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
              <Col span={12}>
                  <Form.Item
                      label="文件名称"
                      name="name"
                      rules={[{ required: true, message: '请输入文件名称' }]}
                  >
                      <Input placeholder="请输入文件名称" data-testid="input-doc-name" />
                  </Form.Item>
              </Col>

              <Col span={12}>
                  <Form.Item
                      label="适用地区"
                      name="nation"
                      rules={[{ required: true, message: '请选择适用地区' }]}
                  >
                      <Select
                          placeholder="请选择适用地区"
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                          }
                          data-testid="select-doc-nation"
                      >
                          {nationOptions.map(option => (
                              <Option key={option.value} value={option.value}>
                                  {option.label}
                              </Option>
                          ))}
                      </Select>
                  </Form.Item>
              </Col>
          </Row>

          <Form.Item label="文件说明" name="remark">
            <TextArea rows={3} placeholder="请输入文件用途或说明" data-testid="input-doc-remark" />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default DocumentManagement;
